<template>
    <div class="ui-page">
        <ui-page-header :title="isNew ? 'Novo perfil' : (form.name || 'Perfil')" back="/perfis" back-text="Perfis de acesso">
            <template #subtitle>
                <span v-if="!isNew && profile">{{ profile.admins }} {{ profile.admins === 1 ? "administrador usa" : "administradores usam" }} este perfil</span>
            </template>
            <v-btn v-if="!isNew && profile" outlined :disabled="saving || removing || profile.admins > 0" :loading="removing"
                :title="profile.admins > 0 ? 'Troque o perfil dos administradores antes de excluir' : ''" @click="remove">
                <v-icon left small>mdi-delete-outline</v-icon>Excluir
            </v-btn>
            <v-btn color="primary" depressed :loading="saving" :disabled="!form.name.trim() || loading" @click="save">Salvar</v-btn>
        </ui-page-header>

        <div v-if="loading" class="pp-loading"><v-progress-circular indeterminate size="24" width="2" color="primary"></v-progress-circular></div>

        <template v-else>
            <ui-section title="Perfil" class="mb-4">
                <v-row dense>
                    <v-col cols="12" sm="5"><v-text-field v-model="form.name" label="Nome" outlined dense hide-details="auto"></v-text-field></v-col>
                    <v-col cols="12" sm="7"><v-text-field v-model="form.description" label="Descrição (opcional)" outlined dense hide-details="auto"></v-text-field></v-col>
                </v-row>
                <v-switch v-model="form.is_super" inset dense hide-details class="mt-4"
                    :label="form.is_super ? 'Acesso total: vê e altera tudo, inclusive perfis e administradores' : 'Acesso total'"></v-switch>
                <p v-if="!form.is_super" class="ui-small ui-muted mt-1 mb-0">
                    Marcar todas as áreas não é o mesmo que acesso total: só o acesso total gerencia perfis, dá acesso total a outros administradores e vale para áreas novas.
                </p>
            </ui-section>

            <ui-section title="Permissões por área"
                description="Ver abre a tela e as consultas; criar, editar e excluir liberam as gravações comuns (e já ligam o ver). As ações específicas, abaixo de cada área, valem em qualquer tela. Áreas sem ver somem do menu.">
                <template #actions>
                    <v-btn small text :disabled="form.is_super" @click="setAll(true)">Marcar tudo</v-btn>
                    <v-btn small text :disabled="form.is_super" @click="setAll(false)">Limpar</v-btn>
                </template>
                <p v-if="form.is_super" class="ui-small ui-muted mb-0">Com acesso total, as permissões por área não se aplicam.</p>
                <table v-else class="pp-matrix">
                    <thead>
                        <tr>
                            <th>Área</th>
                            <th v-for="action in actions" :key="action.key" class="c">{{ action.label }}</th>
                            <th class="c">Tudo</th>
                        </tr>
                    </thead>
                    <tbody v-for="group in groups" :key="group.name">
                        <tr class="pp-matrix__group">
                            <td>{{ group.name }}</td>
                            <td v-for="action in actions" :key="action.key" class="c">
                                <v-simple-checkbox v-if="action.key === 'view' || group.areas.some((area) => area.crud)" :value="groupState(group, action.key) === true"
                                    :indeterminate="groupState(group, action.key) === null" :ripple="false" dense
                                    @input="setGroup(group, action.key, groupState(group, action.key) !== true)"></v-simple-checkbox>
                                <span v-else class="ui-muted">—</span>
                            </td>
                            <td></td>
                        </tr>
                        <template v-for="area in group.areas">
                            <tr :key="area.key">
                                <td class="pp-matrix__area">{{ area.label }}</td>
                                <td v-for="action in actions" :key="action.key" class="c">
                                    <v-simple-checkbox v-if="action.key === 'view' || area.crud" :value="flag(area.key, action.key)" :ripple="false" dense
                                        @input="(value) => setFlag(area.key, action.key, value)"></v-simple-checkbox>
                                    <span v-else class="ui-muted" title="Nesta área as gravações são as ações específicas abaixo">—</span>
                                </td>
                                <td class="c">
                                    <v-simple-checkbox :value="rowAll(area)" :ripple="false" dense @input="(value) => setRow(area, value)"></v-simple-checkbox>
                                </td>
                            </tr>
                            <tr v-if="area.capabilities && area.capabilities.length" :key="`${area.key}-caps`" class="pp-matrix__caps">
                                <td :colspan="actions.length + 2">
                                    <div class="pp-caps">
                                        <span class="pp-caps__title">Ações específicas</span>
                                        <v-checkbox v-for="capability in area.capabilities" :key="capability.key" :input-value="hasCapability(area.key, capability.key)"
                                            :label="capability.label" dense hide-details class="pp-caps__item" @change="(value) => setCapability(area.key, capability.key, value)"></v-checkbox>
                                    </div>
                                </td>
                            </tr>
                        </template>
                    </tbody>
                </table>
            </ui-section>
        </template>
    </div>
</template>

<script>
import axios from "axios";

const EMPTY = () => ({ view: false, create: false, edit: false, delete: false, capabilities: [] });

// Cadastro de um perfil de acesso: nome, acesso total e a matriz area x acao.
export default {
    name: "ProfileDetails",

    data: () => ({
        loading: true,
        saving: false,
        removing: false,
        profile: null,
        areas: [],
        actions: [],
        form: { name: "", description: "", is_super: false, permissions: {} },
    }),

    computed: {
        isNew() {
            return this.$route.params.id === "novo";
        },
        groups() {
            const map = new Map();
            this.areas.forEach((area) => {
                if (!map.has(area.group)) map.set(area.group, { name: area.group, areas: [] });
                map.get(area.group).areas.push(area);
            });
            return [...map.values()];
        },
    },

    watch: {
        "$route.params.id"() {
            this.load();
        },
    },

    created() {
        this.load();
    },

    methods: {
        async load() {
            this.loading = true;
            try {
                const requests = [axios.get("/api/admin/profiles/catalog")];
                if (!this.isNew) requests.push(axios.get(`/api/admin/profiles/${this.$route.params.id}`));
                const [catalog, profile] = await Promise.all(requests);
                this.areas = catalog.data.content.areas;
                this.actions = catalog.data.content.actions;
                this.profile = profile ? profile.data.content : null;
                const source = this.profile || {};
                const permissions = {};
                this.areas.forEach((area) => {
                    const saved = (source.permissions || {})[area.key] || {};
                    permissions[area.key] = { ...EMPTY(), ...saved, capabilities: [...(saved.capabilities || [])] };
                });
                this.form = { name: source.name || "", description: source.description || "", is_super: !!source.is_super, permissions };
            } catch (error) {
                this.notifyError(error);
            } finally {
                this.loading = false;
            }
        },

        flag(area, action) {
            return !!(this.form.permissions[area] && this.form.permissions[area][action]);
        },

        // Criar/editar/excluir ligam o ver; desligar o ver desliga criar/editar/excluir
        // (as acoes especificas valem sozinhas e ficam como estao).
        setFlag(area, action, value) {
            const current = this.form.permissions[area];
            const flags = { ...current, [action]: value };
            if (value && action !== "view") flags.view = true;
            if (!value && action === "view") Object.assign(flags, { view: false, create: false, edit: false, delete: false });
            this.$set(this.form.permissions, area, flags);
        },

        hasCapability(area, key) {
            return (this.form.permissions[area].capabilities || []).includes(key);
        },

        setCapability(area, key, value) {
            const list = (this.form.permissions[area].capabilities || []).filter((item) => item !== key);
            if (value) list.push(key);
            this.$set(this.form.permissions, area, { ...this.form.permissions[area], capabilities: list });
        },

        // Colunas que valem na area (sem criar/editar/excluir onde so ha acoes especificas).
        areaActions(area) {
            return this.actions.filter((action) => action.key === "view" || area.crud).map((action) => action.key);
        },

        rowAll(area) {
            return this.areaActions(area).every((action) => this.flag(area.key, action))
                && (area.capabilities || []).every((capability) => this.hasCapability(area.key, capability.key));
        },

        setRow(area, value) {
            const flags = { ...EMPTY() };
            this.areaActions(area).forEach((action) => { flags[action] = value; });
            flags.capabilities = value ? (area.capabilities || []).map((capability) => capability.key) : [];
            this.$set(this.form.permissions, area.key, flags);
        },

        // true (todas), false (nenhuma) ou null (algumas) na coluna do grupo.
        groupState(group, action) {
            const areas = group.areas.filter((area) => action === "view" || area.crud);
            if (!areas.length) return false;
            const values = areas.map((area) => this.flag(area.key, action));
            if (values.every(Boolean)) return true;
            return values.some(Boolean) ? null : false;
        },

        setGroup(group, action, value) {
            group.areas.filter((area) => action === "view" || area.crud).forEach((area) => this.setFlag(area.key, action, value));
        },

        setAll(value) {
            this.areas.forEach((area) => this.setRow(area, value));
        },

        payload() {
            const permissions = {};
            Object.entries(this.form.permissions).forEach(([area, flags]) => {
                const { capabilities, ...actions } = flags;
                if (Object.values(actions).some(Boolean) || (capabilities && capabilities.length)) permissions[area] = flags;
            });
            return { name: this.form.name, description: this.form.description, is_super: this.form.is_super, permissions };
        },

        async save() {
            this.saving = true;
            try {
                const result = this.isNew
                    ? await axios.post("/api/admin/profiles", this.payload())
                    : await axios.patch(`/api/admin/profiles/${this.profile.id}`, this.payload());
                this.$dialog.notify.success("Perfil salvo.", { position: "top-right", timeout: 3000 });
                if (this.isNew) this.$router.replace(`/perfis/${result.data.content.id}`);
                else this.profile = result.data.content;
            } catch (error) {
                this.notifyError(error);
            } finally {
                this.saving = false;
            }
        },

        async remove() {
            const confirmed = await this.$dialog.confirm({
                title: "Excluir perfil",
                text: `O perfil "${this.profile.name}" será excluído. Confirma?`,
                actions: { false: "Cancelar", true: { text: "Excluir", color: "error" } },
            });
            if (!confirmed) return;
            this.removing = true;
            try {
                await axios.delete(`/api/admin/profiles/${this.profile.id}`);
                this.$dialog.notify.success("Perfil excluído.", { position: "top-right", timeout: 3000 });
                this.$router.replace("/perfis");
            } catch (error) {
                this.notifyError(error);
            } finally {
                this.removing = false;
            }
        },

        notifyError(error) {
            const data = error && error.response && error.response.data;
            const message = (data && data.content && data.content.error && data.content.error.message) || "Não foi possível concluir. Tente novamente.";
            this.$dialog.notify.error(message, { position: "top-right", timeout: 5000 });
        },
    },
};
</script>

<style lang="scss" scoped>
@import "../../assets/scss/ui/tokens";

.pp-loading {
    display: flex;
    justify-content: center;
    padding: 48px 0;
}

.pp-matrix {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;

    th {
        padding: 8px;
        text-align: left;
        font-weight: 600;
        color: $ui-muted;
        border-bottom: 1px solid $ui-border;
    }

    td {
        padding: 2px 8px;
        border-bottom: 1px solid $ui-border-soft;
    }

    .c {
        width: 84px;
        text-align: center;

        ::v-deep .v-simple-checkbox {
            display: inline-flex;
        }
    }
}

.pp-matrix__group td {
    padding-top: 14px;
    font-weight: 600;
    color: $ui-fg;
    background: $ui-bg-muted;
}

.pp-matrix__area {
    padding-left: 20px !important;
}

.pp-matrix__caps td {
    padding: 2px 8px 10px 32px;
}

.pp-caps {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 2px 18px;
}

.pp-caps__title {
    width: 100%;
    font-size: 12px;
    color: $ui-muted;
}

.pp-caps__item {
    margin-top: 0;
    padding-top: 0;

    ::v-deep .v-label {
        font-size: 13px;
    }
}
</style>
